import type { ReactNode } from 'react';

import { useReveal } from '../lib/use-motion.js';

/**
 * Scroll-reveal wrapper — the marketing site's entrance motion.
 *
 * Extracted from `routes/index.tsx` when `features`, `pricing` and `about`
 * became the second, third and fourth callers, per the extract-before-the-
 * second-copy rule. The home page was rebuilt cinematic in July and the rest of
 * the site was not, so the three pages a prospect visits AFTER the home page
 * were visibly plainer than the one that sold them — the same site in two
 * different levels of finish.
 *
 * The visual work all lives in `styles/globals.css` (`.reveal`, `.reveal-left`,
 * `.reveal-right`, `.reveal-scale`, `[data-stagger]`), which already carries a
 * `prefers-reduced-motion` block that disables the whole system. This component
 * only owns the observer and the `is-in` flip.
 *
 * `html.js` gates every rule, so with JavaScript off the content is simply
 * visible rather than stuck at opacity zero — a reveal that never fires must
 * never be the reason a page looks empty.
 */
/**
 * Always a `div`, deliberately.
 *
 * A polymorphic `as` prop was tried and dropped: it forces the observer ref to
 * satisfy every element type at once, which does not typecheck without casts,
 * and it buys nothing — landmark structure is kept by putting this INSIDE the
 * `<section>` it animates rather than by becoming one.
 */
export function RevealBlock({
  children,
  className,
  style,
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}) {
  const { ref, inView } = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`reveal${className ? ` ${className}` : ''}${inView ? ' is-in' : ''}`}
      style={style}
    >
      {children}
    </div>
  );
}
